import {
  AISLE_SIZE,
  EXIT_ROW_SEAT_GROUP,
  SEAT_CODE_AISLE,
  SEAT_CODE_BASSINET,
  SEAT_CODE_BETTER_LEG_ROOM,
  SEAT_CODE_BOARDING_ZONE,
  SEAT_CODE_EXIT_ROW,
  SEAT_CODE_LEGROOM,
  SEAT_CODE_MORE_LEG_ROOM,
  SEAT_CODE_RESTRICTED_RECLINE,
  SEAT_CODE_STANDARD_LEG_ROOM,
  SEAT_CODE_WINDOW,
  SEAT_CODE_WINDOW_NO_WINDOW,
} from '@constants/app.constants';
import { SeatIconType, SeatProperties } from '@models/app.models';
import { createSelector } from '@ngrx/store';
import { extractDigits, getSeatType, getUnitKey, stringAsBooleanProp, } from '@utils/data-utils';
import {
  CompartmentInformationWithRows,
  PassengerSeat,
  RowInformation,
  SeatMapAvailabiliyWithRows,
  UnitInformation,
  UnitType,
} from 'src/app/models/booking.models';
import { selectSeatFeatureState } from '../reducers';
import {
  selectBookingSeats,
  selectBookingSeatsTotalCost,
  selectBookingSegmentsThatCanSellSeats,
  selectBookingSsrAvailability,
  selectBookingTripBreakdown,
  selectOriginalBookingSeats,
  selectPassengers,
  selectSegments,
} from './booking.selector';
import { selectIsCheckInPath } from './router.selector';
import { generateRandomReservedSeats } from '@utils/seats-utils';
import { PassengerInitialsPipe } from "@pipes/passenger-initials/passenger-initials.pipe";

export const selectSeatMaps = createSelector(
  selectSeatFeatureState,
  (state) => state.seatMaps,
);

export const selectActiveSegment = createSelector(
  selectSeatFeatureState,
  (state) => state.activeSegment,
);

export const selectActivePassenger = createSelector(
  selectSeatFeatureState,
  (state) => state.activePassenger,
);

export const selectActiveBookingSegment = createSelector(
  selectActiveSegment,
  selectBookingSegmentsThatCanSellSeats,
  (segmentKey, segments) => {
    return segments?.find((seg) => seg.segmentKey === segmentKey);
  },
);

export const selectActiveSeatMap = createSelector(
  selectActiveBookingSegment,
  selectSeatMaps,
  (segment, seatMaps) => {
    if (!segment || !seatMaps) {
      return null;
    }
    const { origin, destination } = segment.designator;
    return seatMaps.find(
      (map) =>
        map.seatMap.departureStation === origin &&
        map.seatMap.arrivalStation === destination,
    );
  },
);

export const selectActiveSeatMapUnitPriceByGroup = createSelector(
  selectActivePassenger,
  selectActiveSeatMap,
  (passengerKey, seatMap) => {
    if (!passengerKey || !seatMap) {
      return {};
    }

    if (!seatMap.fees?.[passengerKey]?.groups) {
      return {};
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return Object.keys(seatMap.fees[passengerKey]!.groups).reduce(
      (feesTotalByGroup, groupNumberStr) => {
        const groupNumber = Number(groupNumberStr);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const group = seatMap.fees[passengerKey]!.groups[groupNumber]!;
        return {
          ...feesTotalByGroup,
          [groupNumber]: group.fees.reduce((feesTotal, fee) => {
            return (
              feesTotal +
              fee.serviceCharges.reduce(
                (chargesTotal, charge) => chargesTotal + charge.amount,
                0,
              )
            );
          }, 0),
        };
      },
      {} as { [groupNumber: number]: number },
    );
  },
);

export const selectSeatMapsWithRows = createSelector(
  selectSeatMaps,
  (seatMaps) => {
    return seatMaps?.map<SeatMapAvailabiliyWithRows>((seatMap) => ({
      ...seatMap,
      seatMap: { ...seatMap.seatMap },
      fees: { ...seatMap.fees },
      sitTogetherEconomyCabin: { ...seatMap.sitTogetherEconomyCabin},
      sitTogetherPremiumCabin: { ...seatMap.sitTogetherPremiumCabin},
      compartments: Object.values(
        Object.values(seatMap.seatMap.decks)[0]?.compartments ?? {},
      ).map((compartment) => {
        // visible meaning the user will see them in the seat map
        const visibleUnits = compartment.units.filter(
          (unit) =>
            unit.assignable ||
            unit.type === UnitType.Lavatory ||
            unit.type === UnitType.LavatoryWithHandicappedFacilities ||
            unit.type === UnitType.Exit ||
            unit.type === UnitType.Wing,
        );

        const assignableUnits = compartment.units.filter(
          (unit) => unit.assignable,
        );

        // ensure seats are sorted by row then column before we continue,
        // this is mostly for special "seats" like bathrooms which
        // are usually not already sorted by their position
        visibleUnits.sort((unitA, unitB) => {
          const rowOrder = unitA.y - unitB.y;
          return rowOrder === 0 ? unitA.x - unitB.x : rowOrder;
        });

        const compartmentMinX = assignableUnits.reduce((minX, unit) => {
          return Math.min(minX, unit.x);
        }, Infinity);

        const compartmentMaxX = assignableUnits.reduce((maxX, unit) => {
          return Math.max(maxX, unit.x + unit.width);
        }, -Infinity);

        const trueCompartmentWidth = compartmentMaxX - compartmentMinX;

        const rows: RowInformation[] = visibleUnits.reduce(
          (reducedRows, unit, unitIdx, units) => {
            const previousUnit = units[unitIdx - 1];

            const isSameRow = unit.y === previousUnit?.y;
            const currentRow = reducedRows[reducedRows.length - 1];

            if (isSameRow && currentRow) {
              // if the previous unit does not align with this unit, add an aisle
              // to create a space between them
              if (
                previousUnit &&
                previousUnit.x + previousUnit.width < unit.x
              ) {
                currentRow.units.push({
                  type: UnitType.Aisle,
                  width: unit.x - (previousUnit.x + previousUnit.width),
                } as UnitInformation);
              }

              if (unit.assignable) {
                currentRow.designator = extractDigits(unit.designator);
              }

              currentRow.units.push({ ...unit });
              return reducedRows;
            }

            if (!isSameRow && currentRow) {
              if (
                previousUnit &&
                previousUnit.x + previousUnit.width - compartmentMinX <
                  trueCompartmentWidth
              ) {
                // last unit of row did not reach end of row, pad right with an aisle
                currentRow.units.push({
                  type: UnitType.Aisle,
                  width:
                    trueCompartmentWidth -
                    (previousUnit.x - compartmentMinX + previousUnit.width),
                } as UnitInformation);
              }
            }

            return reducedRows.concat({
              order: unit.y,
              designator: '',
              width: trueCompartmentWidth,
              rowOffset: 0,
              // if the unit is not aligned to the left of the row but is the
              // first unit in the row, plce an aisle to the left to space it correctly
              units:
                unit.x > compartmentMinX
                  ? [
                      {
                        type: UnitType.Aisle,
                        width: unit.x - compartmentMinX,
                      },
                      { ...unit },
                    ]
                  : [{ ...unit }],
            } as RowInformation);
          },
          [] as RowInformation[],
        );

        // all rows which are filled only with assignable seats
        // we assume that this is the width of the cabin
        const entirelyAssignableRows = rows.filter((row) =>
          row.units.every((u) => u.type === UnitType.Aisle || u.assignable),
        );

        // loop through the first entirely assignable row and reduce the aisle sizes to the max
        // calculate the difference and distribute the extra space to the seats in all rows on this compartment.
        // get the new seat size and set all seats on this compartment to the new size
        // then evenly remove the space added to the seats from the aisles on the row

        // the number of units (including aisles) in the longest
        // row of assignable seats
        const longestRowOfOnlyAssignableSeatsLength =
          entirelyAssignableRows.reduce(
            (runningMaxRowLength, row) =>
              Math.max(runningMaxRowLength, row.units.length),
            0,
          );

        // these are the rows which have the maximum number of assignable seats
        const entirelyAssignableRowsFullOfSeats = entirelyAssignableRows.filter(
          (row) => row.units.length === longestRowOfOnlyAssignableSeatsLength,
        );

        const firstFullAssignableRow = entirelyAssignableRowsFullOfSeats[0];

        const firstAssignableRowAisleUnits =
          firstFullAssignableRow?.units?.filter(
            (unit) => unit.type === UnitType.Aisle,
          ) ?? [];
        const firstAssignableRowAssignableUnits =
          firstFullAssignableRow?.units?.filter((unit) => unit.assignable) ??
          [];

        // we set the width of the aisle units to our smal constant width to create
        // more space for the seats to display.
        // we need a row with only aisles and assignable seats so we can figure out
        // how large all of the seats should now be
        firstAssignableRowAisleUnits.forEach((unit) => {
          unit.width = AISLE_SIZE;
        });

        // the total width of all aisles is now the number of aisles times our constants
        const totalAislesWidth =
          firstAssignableRowAisleUnits.length * AISLE_SIZE;
        // we can get the remaining space for seats by subtracting from the row width
        const remainingWidthForSeats = firstFullAssignableRow
          ? firstFullAssignableRow.width - totalAislesWidth
          : 0;
        // we then divide this evenly among the seats
        const newUnitSize =
          remainingWidthForSeats / firstAssignableRowAssignableUnits.length;

        // from now on the algorithm is reversed, we use our new seat size on all seats,
        // and then shrink any aisles to give the seats space
        rows.forEach((row) => {
          const aisleUnits = row.units.filter(
            (unit) => unit.type === UnitType.Aisle,
          );
          const assignableUnits = row.units.filter((unit) => unit.assignable);
          assignableUnits.forEach((unit) => {
            unit.width = newUnitSize;
          });
          const currentUnitsWidth = row.units
            .filter((unit) => {
              return unit.type !== UnitType.Exit && unit.type !== UnitType.Wing;
            })
            .reduce((totalWidth, unit) => {
              return totalWidth + unit.width;
            }, 0);
          const extraWidthToRemoveFromAisles = currentUnitsWidth - row.width;
          aisleUnits.forEach((unit) => {
            unit.width -= extraWidthToRemoveFromAisles / aisleUnits.length;
          });
        });

        // we assume the row spacing is based on the spacing between the first
        // two full width assignable-seat-only rows
        const rowSpacing =
          entirelyAssignableRowsFullOfSeats.length >= 2
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              entirelyAssignableRowsFullOfSeats[1]!.order -
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              entirelyAssignableRowsFullOfSeats[0]!.order
            : 0;

        rows.forEach((row, i, arr) => {
          const previousRow = arr[i - 1];
          if (previousRow) {
            previousRow.rowOffset =
              (row.designator && row.designator === previousRow.designator) ||
              previousRow.units[0]?.type === UnitType.Aisle
                ? 1
                : 0;
          }
        });

        return {
          ...compartment,
          rows,
          rowSpacing,
          // the number of rows actually rendered in terms of height,
          // will be one for every row that has no offset, and one for
          // every two rows in a row that do have an offset
          // i.e. an offset row + a non-offset row will be 1 row.
          // two offset rows will be one row
          // non-offset rows are one row.
          // this is an approximation
          totalHeightInRows: rows.filter((row, idx) => {
            const previousRow = rows[idx - 1];
            return (
              idx === 0 ||
              row.rowOffset === 0 ||
              (row.rowOffset !== 0 && (previousRow?.rowOffset ?? 0) !== 0)
            );
          }).length,
        } as CompartmentInformationWithRows;
      }),
    }));
  },
);

export const selectActiveSeatMapWithRows = createSelector(
  selectActiveBookingSegment,
  selectSeatMapsWithRows,
  (segment, seatMapsWithRows) => {
    if (!segment || !seatMapsWithRows) {
      return null;
    }
    const { origin, destination } = segment.designator;
    return seatMapsWithRows.find(
      (map) =>
        map.seatMap.departureStation === origin &&
        map.seatMap.arrivalStation === destination,
    );
  },
);

export const selectActiveSeatMapUnits = (withRandomReservedSeats?: boolean) =>
  createSelector(selectActiveSeatMapWithRows, (seatMap) => {
    if (!seatMap) {
      return [];
    }
    const units = Object.values(seatMap.compartments).flatMap((compartment) => {
      return Object.values(compartment.rows).flatMap((row) => {
        return row.units;
      });
    });

    const randomUnits = generateRandomReservedSeats({
      units,
      economyPercent: {
        firstRange: {
          min: 120,
          percent: 0.4,
        },
        secondRange: {
          min: 100,
          max: 120,
          percent: 0.3,
        },
        thirdRange: {
          min: 75,
          max: 100,
          percent: 0.2,
        },
      },
      premiumPercent: {
        firstRange: {
          min: 20,
          percent: 0.4,
        },
      },
    });

    return withRandomReservedSeats ? randomUnits : units;
  });

export const selectActiveSeatMapSeats = createSelector(
  selectActiveSeatMapUnits(),
  (units) => {
    return units.filter((unit) => unit.assignable);
  },
);

export const selectSeatTotalPriceByUnitKey = createSelector(
  selectActiveSeatMapSeats,
  selectActiveSeatMapUnitPriceByGroup,
  (seats, unitPriceByGroup) => {
    return seats?.reduce(
      (priceByUnitKey, seat) => {
        return {
          ...priceByUnitKey,
          [getUnitKey(seat)]: unitPriceByGroup[seat.group] ?? 0,
        };
      },
      {} as { [unitKey: string]: number },
    );
  },
);

export const selectSelectedSegmentIndex = createSelector(
  selectActiveSegment,
  selectBookingSegmentsThatCanSellSeats,
  (segmentKey, segments) => {
    if (!segmentKey) {
      return 0;
    }
    return segments?.map((s) => s.segmentKey).indexOf(segmentKey) ?? 0;
  },
);

export const selectSeatUserSelections = createSelector(
  selectSeatFeatureState,
  (state) => state.selections,
);

export const selectPassengerSelectedSeats = createSelector(
  selectSeatUserSelections,
  (selections) => selections.seats,
);

export const selectPassengerSelectedSeatSsrs = createSelector(
  selectSeatUserSelections,
  (selections) => selections.ssrs,
);

export const selectAllSeatMapSeatsBySegment = createSelector(
  selectSeatMaps,
  selectSegments,
  (seatMaps, segments) => {
    if (!seatMaps || !segments) {
      return {};
    }
    return segments.reduce(
      (seatsBySegment, segment) => {
        const seatMap = seatMaps.find(
          (map) =>
            map.seatMap.departureStation === segment.designator.origin &&
            map.seatMap.arrivalStation === segment.designator.destination,
        );
        if (!seatMap) {
          return seatsBySegment;
        }
        return {
          ...seatsBySegment,
          [segment.segmentKey]: Object.values(seatMap.seatMap.decks).reduce(
            (seatMapUnits, deck) => {
              const thisDeckUnits = Object.values(deck.compartments).reduce(
                (deckUnits, compartment) => {
                  return deckUnits.concat(compartment.units);
                },
                [] as UnitInformation[],
              );
              return seatMapUnits.concat(thisDeckUnits);
            },
            [] as UnitInformation[],
          ),
        };
      },
      {} as { [key: string]: UnitInformation[] },
    );
  },
);

export const selectBookingPaidSeatsSsrsTotalCost = createSelector(
  selectBookingSeats,
  (seatsSsrs) =>
    seatsSsrs?.reduce((seatsTotal, journeySeat) => {
      return (
        seatsTotal +
        journeySeat.seatSsr.reduce(
          (journeySeatsTotal, passengerSeat) => {
            return (
              journeySeatsTotal +
              passengerSeat.seats.reduce((passengerSeatsTotal, seat) => {
                return (
                  passengerSeatsTotal +
                  seat.ssrs.reduce((seatSsrTotal, ssr) => {
                    return seatSsrTotal + (ssr.paid ? ssr.price : 0);
                  }, 0)
                );
              }, 0)
            );
          },
          0,
        )
      );
    }, 0) ?? 0,
);

export const selectBookingExtrasTotalCost = createSelector(
  selectBookingTripBreakdown,
  (breakdown) => breakdown?.categoryTotals.seats?.totalPrice ?? 0,
);

export const selectSeatsTotalCostTripSummary = createSelector(
  selectPassengerSelectedSeats,
  selectOriginalBookingSeats,
  selectSeatMaps,
  selectSegments,
  selectAllSeatMapSeatsBySegment,
  selectBookingSeatsTotalCost,
  selectPassengerSelectedSeatSsrs,
  selectBookingSsrAvailability,
  selectBookingSeats,
  (
    selectedSeats,
    originalBookingSeats,
    seatMaps,
    segments,
    seatMapsBySegment,
    bookingSeatsTotalCost,
    selectedSeatSsrs,
    ssrAvailability,
    bookingSeats,
  ) => {
    if (!segments || !seatMaps) {
      return bookingSeatsTotalCost;
    }

    let totalSsrCost = 0;
    selectedSeatSsrs?.forEach((s) => {
      const journeySsrs = ssrAvailability?.journeySsrs.find((j) => j.journeyKey === s.journeyKey);
      if (journeySsrs) {
        s.passengers.forEach((p) => {
          p.ssrs.forEach((ssr) => {
            if (ssr.count > 0) {    
                let price = journeySsrs
                ?.seatsSsrs.find((seatSsr) => seatSsr.ssrCode === ssr.ssrCode)
                ?.passengersAvailability.find((pa) => pa.passengerKey === p.passengerKey)
                ?.price;

                if (!price) {
                  price = bookingSeats?.find((seat) => seat.journeyKey === s.journeyKey)
                    ?.seatSsr.find((paxSeat) => paxSeat.passengerKey === p.passengerKey)
                    ?.seats.find((ssr) => ssr.ssrCode === ssr.ssrCode)
                    ?.totalPrice ?? 0;
                }

                totalSsrCost += ssr.count * price;
            }
          });
        });
      }
    });

    const selectedSeatsCost = Object.keys(selectedSeats).reduce(
      (runningTotalOfSegments, segmentKey) => {
        const segmentSeats = selectedSeats[segmentKey];
        if (!segmentSeats) {
          return runningTotalOfSegments;
        }

        const segment = segments.find((seg) => seg.segmentKey === segmentKey);
        if (!segment) {
          return runningTotalOfSegments;
        }

        const seatMap = seatMaps.find(
          (map) =>
            map.seatMap.departureStation === segment.designator.origin &&
            map.seatMap.arrivalStation === segment.designator.destination,
        );
        if (!seatMap) {
          return runningTotalOfSegments;
        }

        const seats = seatMapsBySegment[segmentKey];

        return (
          runningTotalOfSegments +
          Object.keys(segmentSeats).reduce(
            (runningTotalOfSegmentSeats, passengerKey) => {
              const passengerSeatSelection = segmentSeats[passengerKey];
              const unit = seats?.find(
                (seat) => seat.unitKey === passengerSeatSelection?.seatKey,
              );

              if (!unit) {
                return runningTotalOfSegmentSeats;
              }

              const originalBookingSeat = originalBookingSeats
                ?.map((bookingSeat) =>
                  bookingSeat.passengerSeats.find(
                    (passengerSeat) =>
                      passengerSeat.segmentKey === segmentKey &&
                      passengerSeat.passengerKey === passengerKey,
                  ),
                )
                .find((seat): seat is PassengerSeat => seat !== undefined);

              // selection is same as seat on original booking, so we show the total fees
              // associated with that seat
              if (
                originalBookingSeat?.unitKey === passengerSeatSelection?.seatKey
              ) {
                return (
                  runningTotalOfSegmentSeats + (originalBookingSeat?.total ?? 0)
                );
              }

              const costOfSelectedSeat =
                seatMap.fees[passengerKey]?.groups?.[unit.group]?.fees?.reduce(
                  (runningTotalAmount, fee) =>
                    runningTotalAmount +
                    fee.serviceCharges.reduce(
                      (runningTotalServiceCharges, serviceCharge) =>
                        runningTotalServiceCharges + serviceCharge.amount || 0,
                      0,
                    ),
                  0,
                ) ?? 0;

              // otherwise take the maximum of the selected and the paid for seat
              // (can't pay less for a seat for the same segment/pax)

              return (
                runningTotalOfSegmentSeats +
                Math.max(costOfSelectedSeat, originalBookingSeat?.total ?? 0)
              );
            },
            0,
          )
        );
      },
      0,
    );

    return selectedSeatsCost + totalSsrCost;
  },
);

export const selectSeatsTotalCostTripSummaryRounded = createSelector(
  selectSeatsTotalCostTripSummary,
  Math.ceil,
);

export const selectActiveBookingPassenger = createSelector(
  selectActivePassenger,
  selectPassengers,
  (paxKey, paxs) => {
    if (!paxKey) {
      return null;
    }
    return paxs?.[paxKey] ?? null;
  },
);

export const selectActiveBookingPassengerInfant = createSelector(
  selectActiveBookingPassenger,
  (pax) => {
    return pax?.infant ?? null;
  },
);

export const selectActivePassengerBookingAssignedSeat = createSelector(
  selectActiveSegment,
  selectActivePassenger,
  selectOriginalBookingSeats,
  (segmentKey, paxKey, seats) => {
    if (!paxKey) {
      return null;
    }
    return (
      seats
        ?.find((seat) => seat.segmentKey === segmentKey)
        ?.passengerSeats?.find((paxSeat) => paxSeat.passengerKey === paxKey) ??
      null
    );
  },
);

export const selectActiveSeatMapUnitTypesByUnitKey = (
  withRandomReservedSeats?: boolean,
) =>
  createSelector(
    selectActiveSeatMapUnits(withRandomReservedSeats),
    selectActiveBookingSegment,
    selectPassengerSelectedSeats,
    selectActivePassengerBookingAssignedSeat,
    selectActiveBookingPassengerInfant,
    selectIsCheckInPath,
    (
      seats,
      selectedSegment,
      selectedSeats,
      bookingAssignedSeat,
      passengerInfant,
      isCheckInFlow,
    ) => {
      return seats.reduce(
        (seatTypeByUnitKey, unit) => {
          const seatType = getSeatType(
            unit,
            selectedSegment,
            selectedSeats,
            bookingAssignedSeat,
            passengerInfant,
            isCheckInFlow,
          );
          return {
            ...seatTypeByUnitKey,
            [getUnitKey(unit)]: seatType,
          };
        },
        {} as { [key: string]: SeatIconType },
      );
    },
  );

export const selectPassengersByUnitKey = createSelector(
  selectPassengerSelectedSeats,
  selectActiveSeatMapWithRows,
  selectActiveSegment,
  selectPassengers,
  (selectedSeats, seatMap, segmentKey, passengers) => {
    if (!seatMap || !selectedSeats || !segmentKey) {
      return {};
    }

    return seatMap.compartments.reduce(
      (compartmentPassengersByUnits, compartment) => {
        return {
          ...compartmentPassengersByUnits,
          ...compartment.units.reduce(
            (passengersByUnits, unit) => {
              const passengerForUnit = Object.keys(
                selectedSeats[segmentKey] ?? {},
              ).find((pKey) => {
                return (
                  selectedSeats[segmentKey]?.[pKey]?.seatKey === unit.unitKey
                );
              });
              if (!passengerForUnit) {
                return passengersByUnits;
              }

              const passengerInitialsPipe = new PassengerInitialsPipe();
              const initials = passengerInitialsPipe.transform(passengers?.[passengerForUnit]?.name);
              const index = String(
                  Object.keys(passengers ?? {})
                    .map((pKey) => pKey)
                    .indexOf(passengerForUnit) + 1);

              return {
                ...passengersByUnits,
                [getUnitKey(unit)]: {
                  index,
                  initials: initials ?? index,
                },

              };
            },
            {} as { [key: string]: { index: string; initials: string } },
          ),
        };
      },
      {} as { [key: string]: { index: string; initials: string } },
    );
  },
);

export const selectPreviousSegmentKey = createSelector(
  selectBookingSegmentsThatCanSellSeats,
  selectSelectedSegmentIndex,
  (segments, selectedSegmentIndex) => {
    const newTabIndex = Math.max(selectedSegmentIndex - 1, 0);
    return segments?.[newTabIndex]?.segmentKey;
  },
);

export const selectNextSegmentKey = createSelector(
  selectBookingSegmentsThatCanSellSeats,
  selectSelectedSegmentIndex,
  (segments, selectedSegmentIndex) => {
    const newTabIndex = Math.min(
      selectedSegmentIndex + 1,
      (segments?.length ?? 1) - 1,
    );
    return segments?.[newTabIndex]?.segmentKey;
  },
);

export const selectNextPassengerKey = createSelector(
  selectActivePassenger,
  selectActiveSegment,
  selectPassengers,
  selectPassengerSelectedSeats,
  selectOriginalBookingSeats,
  (
    activePassengerKey,
    activeSegmentKey,
    passengers,
    selectedSeats,
    originalBookingSeats,
  ) => {
    const passengerKeys = Object.keys(passengers ?? []);

    // Next passenger that has a selected unit that matches what's on the booking,
    // or that has no unit selected yet.
    const keyOfNextPassengerThatHasNotBeenEditedYet = passengerKeys.find(
      (pKey) => {
        const matchingSeatFromBooking = originalBookingSeats?.find((seat) => {
          return (
            seat.segmentKey === activeSegmentKey &&
            seat.passengerSeats.find((passSeat) => {
              return (
                passSeat.passengerKey === pKey &&
                activeSegmentKey &&
                selectedSeats?.[activeSegmentKey] &&
                passSeat.unitKey ===
                  selectedSeats?.[activeSegmentKey]?.[pKey]?.seatKey
              );
            })
          );
        });
        return (
          pKey !== activePassengerKey &&
          (!activeSegmentKey ||
            (selectedSeats?.[activeSegmentKey]?.[pKey] === undefined &&
              matchingSeatFromBooking === undefined) ||
            matchingSeatFromBooking)
        );
      },
    );

    return keyOfNextPassengerThatHasNotBeenEditedYet ?? activePassengerKey;
  },
);

export const selectNextPassengerKeyByLoop = createSelector(
  selectActivePassenger,
  selectPassengers,
  (
    activePassengerKey,
    passengers,
  ) => {
    const passengerKeys = Object.keys(passengers ?? []);

    if (activePassengerKey) {
      const activePassengerKeyIndex = passengerKeys.indexOf(activePassengerKey);
      const nextPassengerKeyIndex =
        activePassengerKeyIndex === passengerKeys.length - 1
          ? 0
          : activePassengerKeyIndex + 1;
      return passengerKeys[nextPassengerKeyIndex];
    }

    return activePassengerKey;
  },
);

export const selectSeatPropertiesByUnitKey = createSelector(
  selectActiveSeatMapSeats,
  (seats) => {
    return seats.reduce(
      (seatPropsByUnitKey, seat) => {
        const seatProperties = {
          boardingZone: '',
          hasBassinet: false,
          hasExtraLegRoom: false,
          hasRetrictedRecline: false,
          hasWindowNoWindow: false,
          isAisleSeat: false,
          isExitRow: false,
          isWindowSeat: false,
          hasBetterLegRoom: false,
          hasMoreLegRoom: false,
          hasStandardLegRoom: false,
        } as SeatProperties;
        seat.properties.forEach((prop) => {
          switch (prop.code) {
            case SEAT_CODE_WINDOW:
              seatProperties.isWindowSeat = stringAsBooleanProp(prop.value);
              break;
            case SEAT_CODE_AISLE:
              seatProperties.isAisleSeat = stringAsBooleanProp(prop.value);
              break;
            case SEAT_CODE_LEGROOM:
              seatProperties.hasExtraLegRoom = stringAsBooleanProp(prop.value);
              break;
            case SEAT_CODE_BASSINET:
              seatProperties.hasBassinet = stringAsBooleanProp(prop.value);
              break;
            case SEAT_CODE_BOARDING_ZONE:
              seatProperties.boardingZone = prop.value;
              break;
            case SEAT_CODE_RESTRICTED_RECLINE:
              seatProperties.hasRetrictedRecline = stringAsBooleanProp(
                prop.value,
              );
              break;
            case SEAT_CODE_WINDOW_NO_WINDOW:
              seatProperties.hasWindowNoWindow = stringAsBooleanProp(
                prop.value,
              );
              break;
            case SEAT_CODE_EXIT_ROW:
              seatProperties.isExitRow = stringAsBooleanProp(prop.value);
              break;
            case SEAT_CODE_STANDARD_LEG_ROOM:
              seatProperties.hasStandardLegRoom = stringAsBooleanProp(
                prop.value,
              );
              break;
            case SEAT_CODE_MORE_LEG_ROOM:
              seatProperties.hasMoreLegRoom = stringAsBooleanProp(prop.value);
              break;
            case SEAT_CODE_BETTER_LEG_ROOM:
              seatProperties.hasBetterLegRoom = stringAsBooleanProp(prop.value);
              break;
          }
        });
        seatProperties.isExitRow =
          seatProperties.isExitRow || seat.group === EXIT_ROW_SEAT_GROUP;
        return {
          ...seatPropsByUnitKey,
          [getUnitKey(seat)]: seatProperties,
        };
      },
      {} as { [unitKey: string]: SeatProperties },
    );
  },
);
